import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Contact";
import { PageHero } from "@/components/site/PageHero";

const STATUS_STYLE = {
  upcoming: "border-[#00E676]/40 text-[#00E676]",
  ongoing: "border-[#FF8800]/40 text-[#FF8800]",
  completed: "border-slate-600/60 text-slate-500",
};

function EventCard({ event, index }) {
  const e = event;
  const gallery = (e.gallery || "").split("\n").filter(Boolean);
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.55, delay: (index % 3) * 0.08 }}
      className="card-beam group rounded-2xl border border-slate-800 bg-[#0D111D]/90 overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:border-[#FF8800]/40"
      data-testid={`event-card-${e.id}`}
    >
      <div className="relative h-44 overflow-hidden bg-grid-tech">
        {e.image ? (
          <img src={e.image} alt={e.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Calendar className="w-10 h-10 text-white/10" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D111D] via-transparent to-transparent" />
        <span className={`absolute top-4 left-4 glass px-3 py-1 rounded-full font-mono-tech text-[10px] tracking-[0.16em] uppercase border ${STATUS_STYLE[e.status] || STATUS_STYLE.upcoming}`}>
          {e.status}
        </span>
        <span className="absolute top-4 right-4 glass px-3 py-1 rounded-full font-mono-tech text-[10px] tracking-[0.14em] text-[#00B0FF] uppercase">
          {e.category}
        </span>
      </div>
      <div className="p-6">
        <h3 className="font-display text-lg font-bold text-white leading-snug">{e.name}</h3>
        <div className="mt-3 space-y-1.5 text-xs text-slate-400">
          {e.date && (
            <p className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-[#FF8800]" />
              {new Date(e.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
              {e.time && ` · ${e.time}`}
            </p>
          )}
          {e.venue && (
            <p className="flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-[#00B0FF]" />
              {e.venue}{e.address ? `, ${e.address}` : ""}
            </p>
          )}
        </div>
        {e.description && <p className="mt-3 text-sm text-slate-400 leading-relaxed line-clamp-3">{e.description}</p>}
        {gallery.length > 0 && (
          <div className="mt-4 flex gap-2 overflow-x-auto">
            {gallery.map((g) => (
              <img key={g} src={g} alt="" loading="lazy" className="w-16 h-16 rounded-lg object-cover border border-white/10" />
            ))}
          </div>
        )}
        {e.registration_link && e.status !== "completed" && (
          <a
            href={e.registration_link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`event-register-${e.id}`}
            className="group/btn mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold text-xs tracking-wide transition-transform duration-300 hover:scale-105"
          >
            REGISTER
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function Events() {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Events | LaunchLab";
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);

  const { upcoming, past } = useMemo(() => {
    if (!events) return { upcoming: [], past: [] };
    const today = new Date().toISOString().slice(0, 10);
    const isPast = (e) => e.status === "completed" || (e.date && e.date < today && e.status === "upcoming") || e.status === "past";
    return {
      upcoming: events.filter((e) => !isPast(e)),
      past: events.filter(isPast),
    };
  }, [events]);

  return (
    <div data-testid="events-page" className="relative w-full max-w-[100vw] overflow-x-clip text-slate-100 min-h-screen">
      <Header />
      <PageHero
        testid="events-hero"
        kicker="LaunchLab Events"
        title="WHERE FOUNDERS"
        highlight="CONNECT."
        subtext="Startup events, workshops, seminars, launches, networking and founder programs from the LaunchLab ecosystem."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {events === null ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
        ) : (
          <>
            <h2 className="font-display text-2xl font-bold text-white mb-8" data-testid="events-upcoming-title">
              UPCOMING <span className="text-gradient-saffron">EVENTS</span>
            </h2>
            {upcoming.length === 0 ? (
              <div className="glass rounded-2xl p-12 text-center text-slate-400 mb-16" data-testid="events-upcoming-empty">
                New events will be announced soon.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-testid="events-upcoming-grid">
                {upcoming.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
              </div>
            )}

            {past.length > 0 && (
              <>
                <h2 className="font-display text-2xl font-bold text-white mb-8" data-testid="events-past-title">
                  PAST <span className="text-slate-500">EVENTS</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-80" data-testid="events-past-grid">
                  {past.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
                </div>
              </>
            )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
